import { useEffect } from "react";
import { Link } from "react-router-dom";
import { MapPin, Copy, ExternalLink, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Overline, Panel, Pill, Stat, copyText } from "@/components/kit";
import { gbp, fmtDate } from "@/lib/api";

const TYPE_TONE = { match: "volt", training: "cyan", social: "amber" };

export default function Overview({ team, overview, reloadTeam, setTab }) {
  const { stats, upcoming } = overview;
  const hubUrl = `${window.location.origin}/t/${team.slug}`;
  useEffect(() => {
    reloadTeam();
  }, [reloadTeam]);

  return (
    <div className="space-y-8">
      <div className="relative overflow-hidden rounded-lg border border-white/[0.08] bg-[#111827] p-6 sm:p-8 grain">
        <div className="absolute -right-10 top-0 h-full w-40 skew-x-[-18deg] opacity-80" style={{ background: team.primary_color }} />
        <div className="relative max-w-xl">
          <Overline>Team admin</Overline>
          <h1 className="mt-2 font-display text-4xl font-black uppercase leading-none tracking-tight text-white sm:text-5xl" data-testid="overview-team-name">{team.name}</h1>
          <p className="mt-2 flex items-center gap-1 text-sm text-slate-400"><MapPin className="h-4 w-4" /> {team.location || "Add your ground in settings"} · {team.sport}</p>
          <div className="mt-6 flex flex-wrap gap-2">
            <Button size="sm" variant="outline" className="rounded-full border-white/15 bg-transparent" onClick={() => copyText(hubUrl, "Hub link copied")} data-testid="copy-hub-link-btn"><Copy className="mr-1 h-3.5 w-3.5" /> Copy fan hub link</Button>
            <Button asChild size="sm" variant="outline" className="rounded-full border-white/15 bg-transparent" data-testid="open-hub-btn"><Link to={`/t/${team.slug}`} target="_blank"><ExternalLink className="mr-1 h-3.5 w-3.5" /> Open hub</Link></Button>
            <Button asChild size="sm" variant="outline" className="rounded-full border-white/15 bg-transparent" data-testid="open-player-app-btn"><Link to={`/t/${team.slug}/players`}><Smartphone className="mr-1 h-3.5 w-3.5" /> Player app</Link></Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4">
        <Stat label="Players" value={stats.players} testid="stat-players" hint={`${stats.staff} staff · ${stats.pending_invites} invited`} />
        <Stat label="Record W-D-L" value={`${stats.record.w}-${stats.record.d}-${stats.record.l}`} testid="stat-record" />
        <Stat label="Fees collected" value={gbp(stats.fees_collected)} testid="stat-fees-collected" />
        <Stat label="Outstanding" value={gbp(stats.fees_outstanding)} testid="stat-fees-outstanding" />
        <Stat label="Fans following" value={stats.fans} testid="stat-fans" />
        <Stat label="Sponsors" value={stats.sponsors} testid="stat-sponsors" />
        <Stat label="Sponsorship value" value={gbp(stats.sponsorship_value)} testid="stat-sponsorship" />
      </div>

      <Panel>
        <div className="flex items-center justify-between">
          <p className="font-display text-2xl font-bold uppercase text-white">Coming up</p>
          <button className="text-sm text-[#CCFF00]" onClick={() => setTab("fixtures")} data-testid="overview-all-fixtures-btn">All fixtures →</button>
        </div>
        <div className="mt-4 divide-y divide-white/5">
          {upcoming.map((e) => (
            <div key={e.id} className="flex flex-wrap items-center justify-between gap-3 py-3" data-testid="overview-upcoming-event">
              <div className="flex items-center gap-3"><Pill tone={TYPE_TONE[e.type]}>{e.type}</Pill><span className="font-semibold text-white">{e.title}</span></div>
              <span className="font-mono text-xs text-slate-400">{fmtDate(e.starts_at)} · {e.venue}</span>
            </div>
          ))}
          {!upcoming.length && <p className="py-3 text-sm text-slate-500">Nothing scheduled. Add a fixture to get started.</p>}
        </div>
      </Panel>
    </div>
  );
}
